import React, { useState, memo } from 'react';
import { ChevronDownIcon } from 'lucide-react';
const faqItems = [{
  question: 'What formats are the books available in?',
  answer: 'The books are available in digital (PDF, instant download) and physical (premium hardcover or paperback, printed on demand). Some bundles include both formats. Digital editions are compatible with all devices — phones, tablets, computers, and e-readers.'
}, {
  question: 'How quickly will I receive my order?',
  answer: "Digital editions are delivered instantly on the thank-you page after checkout and also sent to your email.\n\nPhysical books are professionally printed on demand and typically arrive within 9–14 days. The Time Traveler's Collector Bundle ships in 8–10 business days. Once your order ships, you'll receive a tracking email automatically."
}, {
  question: 'Is this official/licensed content?',
  answer: 'No. This is an independent fan publication created to celebrate film history and cultural impact. It is not affiliated with, endorsed by, or licensed by Universal City Studios LLC or Amblin Entertainment. "Back to the Future™" is a trademark of Universal City Studios LLC.\n\nAll original written material, design, commentary, and analysis are protected under copyright as original works of authorship owned by Legendary Reads.'
}, {
  question: 'What makes this different from other BTTF books?',
  answer: "Because it wasn't made to be fast or disposable.\n\nThis collection took two years of deep research, careful writing, multiple revisions, and a clear editorial vision. It is not recycled Wikipedia content, not AI-generated filler, and not a coffee table book with photos you've seen a thousand times.\n\nIt's the comprehensive deep-dive true fans have been waiting for."
}, {
  question: 'Why only 1,985 copies?',
  answer: "1985 is the year Back to the Future was released. The physical edition is limited to exactly 1,985 copies as a tribute to that moment — making each copy a genuine collector's item. Once they're gone, they won't be reprinted."
}, {
  question: 'Can I gift this to someone?',
  answer: "Yes. Simply enter the recipient's shipping address at checkout for physical editions. For digital editions, the download link is sent to the email used at checkout — you can forward it to the recipient or purchase using their email address."
}, {
  question: 'Are there future updates or additions?',
  answer: 'Digital edition buyers may receive updates if new content is added to the collection. Physical editions are fixed at the time of printing. We recommend joining our email list to stay informed about any future releases or companion volumes.'
}, {
  question: 'Who wrote this book?',
  answer: 'This collection was written by The VHS Club, a dedicated team of writers and researchers who spent years researching, organizing, and refining the content.\n\nThis was not rushed, outsourced, or assembled from recycled material — it was created intentionally for true fans.'
}, {
  question: 'Is this book generated entirely by AI?',
  answer: 'No.\n\nAll research, writing, analysis, and structure were created by humans over an extended period of time. AI tools were only used in limited ways, such as formatting, organization, and final layout assistance.\n\nAI was used as a tool, not as the author.'
}, {
  question: 'Is there an audiobook version?',
  answer: "Yes. The Back In Time Audiobook Experience is included exclusively in the Time Traveler's Collector Bundle. It's a professionally produced audio version of the main book — perfect for listening on the go."
}, {
  question: 'What if I have an issue with my order?',
  answer: "Please contact us directly at info.legendaryreads@gmail.com and we'll be happy to help.\n\nFor the fastest resolution, we always recommend reaching out to us before contacting your bank or card provider."
}];
const FAQItem = ({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  return <div className="border border-bttf-accent/20 rounded-lg overflow-hidden transition-colors duration-200" data-id="element-148">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 md:px-6 md:py-5 text-left bg-bttf-bg/60 hover:bg-bttf-bg/80 transition-colors duration-200 cursor-pointer" aria-expanded={isOpen} data-id="element-149">
        <span className="text-base md:text-lg font-semibold text-bttf-text pr-4" data-id="element-150">
          {question}
        </span>
        <ChevronDownIcon className={`h-5 w-5 text-bttf-primary flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-151" />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-152">
        <div className="px-5 pb-5 md:px-6 md:pb-6 pt-1 bg-bttf-bg/40" data-id="element-153">
          {answer.split('\n\n').map((paragraph, i) => <p key={i} className="text-bttf-subtext text-sm md:text-base leading-relaxed mb-3 last:mb-0" data-id="element-154">
              {paragraph}
            </p>)}
        </div>
      </div>
    </div>;
};
export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-4 md:py-8 bg-bttf-bg text-bttf-text relative overflow-hidden" data-id="element-155">
      <div className="absolute inset-0 bg-bttf-highlight/5 mix-blend-overlay" data-id="element-156"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-157">
        <div className="text-center mb-4" data-id="element-158">
          <h2 className="text-2xl md:text-3xl font-bold font-future text-bttf-text mb-2" data-id="element-159">
            Questions? We've Got Answers.
          </h2>
          <div className="w-20 h-1 bg-bttf-primary mx-auto" data-id="element-160"></div>
        </div>

        <div className="max-w-3xl mx-auto space-y-3" data-id="element-161">
          {faqItems.map((item, index) => <FAQItem key={index} question={item.question} answer={item.answer} isOpen={openIndex === index} onToggle={() => handleToggle(index)} data-id="element-162" />)}
        </div>
      </div>
    </section>;
};
const StarIcon = ({
  className
}: {
  className: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-163">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" data-id="element-164"></polygon>
  </svg>;